import React, { ReactNode, useCallback, useState } from 'react';
import { Download, Wallet, X } from '@phosphor-icons/react';
import { ButtonCommon, UploadFileCommon, FancySpinner, FileUploadModal } from 'components/common';
import { DebtPaymentInfo, DebtSectionHeader, DebtStatusTag } from './DebtDetailCommon';
import { format, isValid } from 'date-fns';
import useDebtDetailOperations from 'hooks/UseDebtDetailOperationsReturn';
import { DebtStatusCode, PaymentType } from 'models';
import useAuthContext from 'stores/auth-context';
import { useMediaQuery } from 'react-responsive';
import dayjs from 'dayjs';

// Helper function to safely format dates
const formatSafeDate = (dateValue: string | Date | null | undefined, formatString: string = 'dd-MM-yyyy'): string => {
  if (!dateValue) return '---';

  const date = new Date(dateValue);
  if (!isValid(date)) return '---';

  try {
    return format(date, formatString);
  } catch (error) {
    console.error('Date formatting error:', error);
    return '---';
  }
};

// Reusable Info Field Component
const InfoField: React.FC<{ label: string; value: ReactNode; isHighlight?: boolean }> = ({
  label,
  value,
  isHighlight = false,
}) => (
  <div>
    <div className='text-sm text-[var(--textBlack56)] mb-1'>{label}</div>
    <div className={`text-sm ${isHighlight ? 'font-bold text-[var(--primaryColor500)]' : 'font-medium'}`}>
      {value || '---'}
    </div>
  </div>
);

const InfoGrid: React.FC<{ children: ReactNode; columns?: number; className?: string }> = ({
  children,
  columns = 4,
  className = '',
}) => <div className={`grid grid-cols-1 md:grid-cols-${columns} gap-4 px-6 py-3 ${className}`}>{children}</div>;

// Main Component
const DebtDetailComponent: React.FC<{
  debtId: string;
  isModalView?: boolean;
  onClose?: () => void;
  onPrint?: () => void;
  onConfirm?: () => void;
  className?: string;
}> = ({ debtId, isModalView = true, onClose, onConfirm: externalOnConfirm, className = '' }) => {
  const {
    paymentMethod,
    debt,
    isLoading,
    filesData,
    saleOrder,
    purchaseOrder,
    transactions,
    setFilesData,
    handleConfirmPayment,
    // handleUpdateDebt,
    setPaymentMethod,
    scheduleDate,
    setScheduleDate,
    handleUpload,
    handleConfirm,
    handleAddOverdue,
    paymentAmount,
    setPaymentAmount,
    handleOnNewFileUpload,
    handleOnRemoveFile,
    handlePrint,
    isLoadingCreateDebtDocument,
    isUpdating,
  } = useDebtDetailOperations({
    debtId,
  });
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = React.useState(false);
  const { loading } = useAuthContext();
  const isDesktop = useMediaQuery({ minWidth: 1280 });
  const [isPaymentPanelVisible, setIsPaymentPanelVisible] = useState(false);

  const togglePaymentPanel = () => {
    setIsPaymentPanelVisible((prev) => !prev);
  };
  // Handle print - use external handler if provided, otherwise use internal
  const handlePrintClick = (): void => {
    if (handlePrint) {
      handlePrint();
    }
  };

  // Handle confirm - use external handler if provided, otherwise use internal
  const handleConfirmClick = useCallback((): void => {
    if (handleConfirm()) {
      if (paymentMethod === PaymentType.NEGOTIATED_PAYMENT) {
        handleAddOverdue();
      } else setIsFileUploadModalOpen(true);
    } else {
      return; // If handleConfirm returns false, do not proceed
    }
  }, [paymentMethod, scheduleDate, handleConfirm, paymentAmount]);
  if (isLoading || loading) {
    return (
      <div className='flex items-center justify-center h-96'>
        <FancySpinner size='large' colorScheme='blue' text='Đang tải dữ liệu...' />
      </div>
    );
  }
  const isOverdue = debt?.deadline && dayjs(debt.deadline).isBefore(dayjs(), 'day');

  const statusCode =
    isOverdue && debt.status.code !== DebtStatusCode.COMPLETED && debt.status.code !== DebtStatusCode.CONFIRMING
      ? DebtStatusCode.OVERDUE
      : debt?.status.code;

  if (!debt) {
    return (
      <div className='flex items-center justify-center h-96'>
        <div className='text-center'>
          <div className='text-lg font-medium text-red-500'>Có lỗi xảy ra</div>
          <div className='text-sm text-gray-500'>Không thể tải thông tin công nợ</div>
        </div>
      </div>
    );
  }
  const renderPaymentPanel = () => (
    <div className='bg-[var(--textBlack2)] p-4 rounded-[8px] min-h-fit border border-[var(--textBlack8)] shadow-sm h-full'>
      <DebtPaymentInfo
        debt={debt}
        debtId={debtId}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        scheduleDate={scheduleDate}
        setScheduleDate={setScheduleDate}
        paymentAmount={paymentAmount}
        setPaymentAmount={setPaymentAmount}
        inventoryDocumentItems={purchaseOrder?.inventoryDocumentItems || []}
        saleOrderItems={saleOrder?.saleOrderItems || []}
        vatRate={(debt?.vat || 0) / 100}
        transactions={transactions}
      />
    </div>
  );
  return (
    <div className={` bg-[var(--bgGreyEy)] w-full ${isModalView ? 'rounded-[16px] pb-4' : ''} ${className}`}>
      {isUpdating && (
        <div className='fixed inset-0 flex items-center justify-center bg-white/70 z-50'>
          <FancySpinner size='large' colorScheme='blue' text='Đang cập nhật dữ liệu...' overlay={true} />
        </div>
      )}
      {isModalView && (
        <div className='flex justify-between items-start py-4 bg-white px-6  rounded-t-[16px] '>
          <div className='flex flex-col gap-2'>
            <h2 className='leading-[24px] text-[20px] font-bold text-[var(--textBlack100)]'>PHIẾU CÔNG NỢ</h2>
            <p className='text-sm text-[var(--textGray)] mb-6'>
              Tất cả các thông tin về công nợ đều được hiển thị tại đây
            </p>
          </div>
          <ButtonCommon loading={isLoadingCreateDebtDocument} color='soft' radius='rounded' onClick={handlePrintClick}>
            <Download size={20} className='mr-2' />
            In phiếu công nợ
          </ButtonCommon>
        </div>
      )}

      <div className='flex flex-row gap-6 bg-[var(--bgGreyEy)] px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto'>
        <div className='flex-1'>
          {/* Company Information */}
          <div>
            <DebtSectionHeader className='!text-[14px] !leading-[20px]' title='Thông tin đối tượng công nợ' />
            <InfoGrid columns={3}>
              <InfoField label='Tên khách hàng' value={debt.companyName} />
              <InfoField label='Mã số thuế' value={debt.taxCode} />
              <InfoField label='Liên hệ' value={debt.phoneNumber} isHighlight={true} />
            </InfoGrid>

            <InfoGrid columns={1} className='mt-2'>
              <InfoField label='Email' value={debt.email} isHighlight={true} />
            </InfoGrid>

            <div className='mt-2 grid grid-cols-1 md:grid-cols-3 gap-4 px-6'>
              <InfoField label='Mã phiếu' value={debt.code} />
              <InfoField label='Ngày lập phiếu' value={formatSafeDate(debt.createdAt)} />
              <div>
                <div className='text-sm text-[var(--textBlack56)] mb-1'>Trạng thái</div>
                <DebtStatusTag status={statusCode} />
              </div>
            </div>

            <InfoGrid columns={3} className='mt-2'>
              <InfoField label='Hạn trả nợ' value={formatSafeDate(debt.deadline)} />
              <InfoField label='Loại giao dịch' value={debt.refTable === 'SaleOrders' ? 'Bán hàng' : 'Nhập hàng'} />
              <InfoField
                label={debt.refTable === 'SaleOrders' ? 'Mã phiếu bán' : 'Mã phiếu nhập'}
                value={saleOrder?.code || purchaseOrder?.code || '---'}
                isHighlight={true}
              />
            </InfoGrid>
            <InfoGrid columns={1} className='mt-2'>
              <InfoField label='Địa chỉ' value={debt.address} />
            </InfoGrid>
          </div>

          {/* Partner Information */}
          <div>
            {/* <DebtSectionHeader className='!text-[14px] !leading-[20px]' title='Thông tin cơ bản' />
            <InfoGrid columns={3}>
              <InfoField label='Tên người mua' value={profile?.data.baseUser.name} />
              <InfoField label='Mã số thuế' value={'---'} />
              <InfoField label='Liên hệ' value={profile?.data.baseUser.phoneNumber} isHighlight={true} />
            </InfoGrid>

            <InfoGrid columns={1} className='mt-2'>
              <InfoField label='Email' value={profile?.data.baseUser.email} isHighlight={true} />
            </InfoGrid> */}
          </div>

          {/* Documents Section */}
          <div>
            <DebtSectionHeader className='!text-[14px] !leading-[20px] mt-2' title='Quản lý chứng từ thanh toán' />
            <div className='pr-4 pt-4'>
              <UploadFileCommon
                filesData={filesData}
                setFilesData={setFilesData}
                accept='image/*,application/pdf'
                maxSizeMB={2}
                maxCount={5}
                onNewFileUpload={handleOnNewFileUpload}
                readOnly={debt.status?.code !== DebtStatusCode.WAITING}
                isDisabled={debt.status?.code !== DebtStatusCode.WAITING}
                onRemoveFile={handleOnRemoveFile}
              />
            </div>
          </div>
        </div>

        {/* Right side - Payment information */}
        {isDesktop && <div className='w-[444px] min-w-[444px]'>{renderPaymentPanel()}</div>}
      </div>

      {/* Mobile Payment Panel Button */}
      {!isDesktop && (
        <div className='fixed bottom-10 right-6 z-40 cursor-pointer' onClick={togglePaymentPanel}>
          <div className='bg-[var(--primaryColor500)] text-white rounded-full max-w-fit px-4 h-16 flex items-center justify-center shadow-lg'>
            <Wallet size={28} weight='fill' /> <div className='text-lg font-bold ml-2'>Thông tin thanh toán</div>
          </div>
        </div>
      )}

      {/* Mobile Payment Panel Overlay */}
      {!isDesktop && (
        <div className={`fixed inset-0 z-50 flex justify-end ${isPaymentPanelVisible ? 'visible' : 'invisible'}`}>
          <div
            className={`absolute inset-0 bg-black transition-opacity duration-300 ${
              isPaymentPanelVisible ? 'opacity-50' : 'opacity-0'
            }`}
            onClick={() => setIsPaymentPanelVisible(false)}
          ></div>

          <div
            className={`relative bg-white min-w-[500px] flex flex-col max-w-[550px] h-full transform transition-transform duration-300 ease-out shadow-xl overflow-auto ${
              isPaymentPanelVisible ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className='sticky top-0 bg-white p-4 border-b border-gray-200 flex items-center justify-between z-10'>
              <div className='text-lg font-bold'></div>
              <button
                className='text-gray-500 hover:text-gray-700 focus:outline-none'
                onClick={() => setIsPaymentPanelVisible(false)}
              >
                <X size={24} />
              </button>
            </div>
            <div className='p-4 flex-1'>{renderPaymentPanel()}</div>
          </div>
        </div>
      )}

      <div className='mt-6 flex gap-4 items-center justify-center'>
        <ButtonCommon color='none' size='sm' radius='rounded' onClick={onClose}>
          Trở lại
        </ButtonCommon>
        {debt.status.code === DebtStatusCode.WAITING && (
          <ButtonCommon color='primary' size='sm' radius='rounded' onClick={handleConfirmClick}>
            Xác nhận
          </ButtonCommon>
        )}
      </div>

      <FileUploadModal
        isOpen={isFileUploadModalOpen}
        onClose={() => {
          setIsFileUploadModalOpen(false);
        }}
        title={'TẢI LÊN CHỨNG TỪ THANH TOÁN'}
        subtitle={'Vui lòng tải lên thông tin hình ảnh và xác nhận với kế toán'}
        acceptedFileTypes={'.xlsx,.xls,.xlsm,.xlm,.png,.jpg,.jpeg,.webp,.gif,.bmp'}
        fileTypesHelpText={'Hỗ trợ các tệp tin excel, png, jpg, gif, bmp, ...'}
        multiple={false}
        positionButton='center'
        confirmText='Xác nhận đã thanh toán'
        typeModal='upload'
        maxFileSize={5 * 1024 * 1024}
        onUpload={handleUpload}
        onComplete={async (files) => {
          console.log('Uploaded files:', files);
          // if (files.length > 0) {
          try {
            await handleConfirmPayment();
            externalOnConfirm?.();
            setIsFileUploadModalOpen(false);
          } catch (error) {
            console.error('Error confirming payment:', error);
          }
          // } else {
          //   console.warn('No files uploaded');
          // }
        }}
      />
    </div>
  );
};

export default DebtDetailComponent;
